import React, { useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import useCachedProducts from "../hooks/UseCachedProducts";
import { useAuth } from "../context/AuthContext";
import { getPrimaryImage } from "../utils/imageUtils";
import { FaLeaf, FaGift } from "react-icons/fa";
import { GiBottleVapors, GiSmokingPipe, GiHemp } from "react-icons/gi";
import { LuCircleHelp, LuSettings, LuList, LuShoppingCart, LuChevronLeft, LuChevronRight } from "react-icons/lu";

// ─── config ────────────────────────────────────────────────────────────────────

const CATEGORY_CONFIG = {
  "disposable-vapes": {
    label:    "Vapes",
    banner:   { tag: "Hot Category", headline: "Next-Gen Disposable Vaping Systems", body: "From 5K to 25K puffs — the widest selection of disposables available in bulk for DFW retailers." },
  },
  kratom: {
    label:    "Kratom",
    banner:   { tag: "Top Seller", headline: "Premium Kratom Extracts & Powders", body: "Gold liquid shots, powder blends, and capsules — stocked in high volume for your store." },
  },
  novelty: {
    label:    "Novelty",
    banner:   { tag: "Impulse Buy", headline: "High-Margin Novelty & Counter Items", body: "Toys, collectibles, and impulse buys proven to move at convenience store counters." },
  },
  glass: {
    label:    "Glass",
    banner:   { tag: "Wholesale", headline: "Quality Glassware Direct from Warehouse", body: "Thick borosilicate pieces, bubblers, and display items at unbeatable wholesale pricing." },
  },
  delta: {
    label:    "Delta",
    banner:   { tag: "Compliant Stock", headline: "Delta Products — Always In Stock", body: "Gummies, vapes, and tinctures sourced from compliant manufacturers. Ready to ship." },
  },
};

const NAV_CATEGORIES = [
  { slug: "disposable-vapes", label: "Vapes",   Icon: GiBottleVapors },
  { slug: "kratom",           label: "Kratom",  Icon: FaLeaf         },
  { slug: "novelty",          label: "Novelty", Icon: FaGift         },
  { slug: "glass",            label: "Glass",   Icon: GiSmokingPipe  },
  { slug: "delta",            label: "Delta",   Icon: GiHemp         },
];

const ITEMS_PER_PAGE = 12;

// ─── component ─────────────────────────────────────────────────────────────────

export default function AllProducts() {
  const { category } = useParams();
  const navigate     = useNavigate();
  const { user }     = useAuth();
  const allProducts  = useCachedProducts();

  const config          = CATEGORY_CONFIG[category] || { label: "All Products", banner: { tag: "Wholesale", headline: "Our Complete Catalog", body: "Browse the full range of wholesale products available for DFW retailers." } };
  const displayCategory = config.label;

  const [sortBy,   setSortBy]   = useState("name-asc");
  const [page,     setPage]     = useState(1);
  const [sideOpen, setSideOpen] = useState(false);

  const filteredProducts = useMemo(() => {
    let list = [...allProducts];
    if (category && CATEGORY_CONFIG[category]) {
      // Match against both the URL slug AND the label so products stored as either
      // "disposable-vapes" or "Vapes" are caught correctly
      const slugLower  = category.toLowerCase();
      const labelLower = config.label.toLowerCase();
      list = list.filter(p => {
        const cat = p.category?.toLowerCase();
        return cat === slugLower || cat === labelLower || cat?.replace(/\s+/g, "-") === slugLower;
      });
    }
    switch (sortBy) {
      case "name-asc":  list.sort((a, b) => a.name?.localeCompare(b.name)); break;
      case "name-desc": list.sort((a, b) => b.name?.localeCompare(a.name)); break;
      case "price-asc": list.sort((a, b) => (a.price || 0) - (b.price || 0)); break;
      case "price-desc":list.sort((a, b) => (b.price || 0) - (a.price || 0)); break;
      default: break;
    }
    return list;
  }, [allProducts, displayCategory, sortBy]);

  const totalPages = Math.ceil(filteredProducts.length / ITEMS_PER_PAGE);
  const paginated  = filteredProducts.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE);

  const goPage = (n) => { setPage(n); window.scrollTo({ top: 0, behavior: "smooth" }); };

  const pageTitle = category
    ? `${config.label} Wholesale | Khoja Distributions — DFW, Texas`
    : "All Wholesale Products | Khoja Distributions — DFW, Texas";
  const pageDesc = category
    ? `${config.banner.body} Shop bulk ${config.label} wholesale pricing at Khoja Distributions, DFW's premier B2B supplier.`
    : "Browse the full wholesale catalog at Khoja Distributions. Vapes, kratom, delta, glass, novelty and more — DFW local delivery in 24–48 hrs.";
  const pageUrl = category
    ? `https://www.khojadistributions.com/products/${category}`
    : "https://www.khojadistributions.com/products";

  return (
    <div className="flex min-h-screen bg-background font-sans">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDesc} />
        <link rel="canonical" href={pageUrl} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDesc} />
        <meta property="og:url" content={pageUrl} />
      </Helmet>

      {/* ── Sidebar overlay (mobile) ────────────────────────────────────────── */}
      {sideOpen && (
        <div className="fixed inset-0 z-40 bg-black/40 md:hidden" onClick={() => setSideOpen(false)} />
      )}

      {/* ── Sidebar ─────────────────────────────────────────────────────────── */}
      <aside className={`
        fixed md:sticky top-0 md:top-[88px] z-50 md:z-auto
        h-screen md:h-[calc(100vh-88px)]
        w-64 bg-card border-r border-border
        flex flex-col py-8 overflow-y-auto shrink-0
        transition-transform duration-300
        ${sideOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"}
      `}>
        <div className="px-6 mb-5">
          <h3 className="text-foreground font-black text-base">Categories</h3>
          <p className="text-[var(--color-muted-foreground)] text-xs mt-0.5">Wholesale Catalog</p>
        </div>

        <nav className="flex flex-col gap-0.5 px-3">
          {NAV_CATEGORIES.map(({ slug, label, Icon }) => {
            const active = category === slug;
            return (
              <Link
                key={slug}
                to={`/products/${slug}`}
                onClick={() => setSideOpen(false)}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-semibold transition-all ${
                  active
                    ? "bg-brand-amber/10 text-brand-amber border border-brand-amber/30"
                    : "text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white hover:bg-slate-50 dark:hover:bg-white/5"
                }`}
              >
                <Icon className="w-4 h-4 flex-shrink-0" size={16} />
                {label}
              </Link>
            );
          })}
        </nav>

        {/* Bulk Pricing card — only shown to guests */}
        {!user && (
          <div className="mx-4 mt-8 bg-muted rounded-xl p-5 border border-border">
            <p className="text-[10px] uppercase tracking-widest text-[var(--color-muted-foreground)] mb-1">Resource</p>
            <h5 className="text-sm font-bold text-foreground mb-3">Bulk Pricing Guide</h5>
            <Link
              to="/support"
              className="block w-full py-2 bg-brand-amber text-white rounded-lg text-xs font-bold text-center hover:brightness-110 transition-all"
            >
              Contact for Pricing
            </Link>
          </div>
        )}

        {/* Bottom links */}
        <div className="mt-auto flex flex-col gap-0.5 px-3 pb-4">
          <Link to="/support" className="flex items-center gap-3 px-3 py-2 text-sm text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white rounded-lg hover:bg-slate-50 dark:hover:bg-white/5 transition-colors">
            <LuCircleHelp size={16} />
            Support
          </Link>
          <Link to="/dashboard" className="flex items-center gap-3 px-3 py-2 text-sm text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white rounded-lg hover:bg-slate-50 dark:hover:bg-white/5 transition-colors">
            <LuSettings size={16} />
            Account
          </Link>
        </div>
      </aside>

      {/* ── Main ────────────────────────────────────────────────────────────── */}
      <main className="flex-1 px-6 md:px-10 py-10 bg-background min-w-0">

        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-xs font-semibold text-brand-muted uppercase tracking-widest mb-6">
          <Link to="/" className="hover:text-brand-text transition-colors">Home</Link>
          <span>/</span>
          <span className="text-brand-amber">{displayCategory}</span>
        </nav>

        {/* Title row */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
          <div>
            <h1 className="text-4xl font-black text-foreground tracking-tight">{displayCategory}</h1>
            <p className="text-[var(--color-muted-foreground)] text-sm mt-1 font-medium">
              {allProducts.length === 0
                ? "Loading products…"
                : `Showing ${filteredProducts.length} wholesale item${filteredProducts.length !== 1 ? "s" : ""}`}
            </p>
          </div>
          <div className="flex items-center gap-3">
            {/* Mobile sidebar toggle */}
            <button
              onClick={() => setSideOpen(true)}
              className="md:hidden flex items-center gap-2 px-4 py-2 rounded-lg border border-border text-sm font-semibold text-[var(--color-muted-foreground)] hover:border-brand-amber hover:text-foreground transition-colors"
            >
              <LuList size={16} />
              Categories
            </button>
            {/* Sort */}
            <div className="flex items-center bg-card border border-border px-4 py-2 rounded-lg gap-2">
              <span className="text-xs font-bold text-[var(--color-muted-foreground)] uppercase">Sort</span>
              <select
                value={sortBy}
                onChange={e => { setSortBy(e.target.value); setPage(1); }}
                className="bg-card border-none text-sm font-bold text-foreground focus:ring-0 p-0 cursor-pointer"
              >
                <option value="name-asc">Name A–Z</option>
                <option value="name-desc">Name Z–A</option>
                <option value="price-asc">Price: Low–High</option>
                <option value="price-desc">Price: High–Low</option>
              </select>
            </div>
          </div>
        </div>

        {/* Featured banner */}
        <div className="relative h-52 rounded-2xl overflow-hidden bg-brand-navy mb-10">
          <div className="absolute inset-0 bg-gradient-to-r from-brand-navy via-brand-navy/80 to-transparent z-10" />
          <div className="relative z-20 flex flex-col justify-center h-full px-10 max-w-xl">
            <span className="inline-block bg-brand-amber/20 border border-brand-amber/40 text-brand-amber text-[10px] font-black uppercase tracking-widest px-3 py-1 rounded-full mb-4 w-fit">
              {config.banner.tag}
            </span>
            <h2 className="text-2xl font-bold text-white mb-2">{config.banner.headline}</h2>
            <p className="text-brand-muted text-sm leading-relaxed">{config.banner.body}</p>
          </div>
          {/* decorative gradient blob */}
          <div className="absolute right-0 top-0 w-1/2 h-full bg-gradient-to-l from-brand-amber/10 to-transparent" />
        </div>

        {/* Grid */}
        {allProducts.length === 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="animate-pulse bg-slate-200 dark:bg-muted rounded-xl aspect-square" />
            ))}
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className="text-center py-20 text-brand-muted">
            <p className="text-lg font-semibold">No products found in this category.</p>
            <Link to="/" className="mt-4 inline-block text-brand-amber font-bold hover:underline">← Back to Home</Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {paginated.map((product, idx) => {
              const outOfStock = !product.quantity || product.quantity <= 0;
              return (
                <Link key={product.id} to={`/products/details/${product.id}`}>
                  <div className={`group relative bg-card rounded-xl p-4 transition-all duration-300 border border-border ${outOfStock ? "opacity-70 cursor-default" : "hover:bg-muted hover:shadow-lg hover:shadow-slate-200/50 dark:hover:shadow-black/30 hover:border-brand-amber/30"}`}>
                    {/* Image */}
                    <div className="relative aspect-square rounded-lg overflow-hidden bg-background mb-4">
                      {idx === 0 && !outOfStock && (
                        <span className="absolute top-3 left-3 z-10 bg-brand-amber text-white text-[10px] font-black uppercase px-2 py-1 rounded shadow-sm">
                          Best Seller
                        </span>
                      )}
                      {outOfStock && (
                        <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/60 dark:bg-black/60">
                          <span className="bg-slate-700 text-white text-[10px] font-black uppercase tracking-wider px-3 py-1.5 rounded-full shadow">
                            Out of Stock
                          </span>
                        </div>
                      )}
                      <img
                        src={getPrimaryImage(product)}
                        alt={product.name}
                        className={`w-full h-full object-contain p-6 transition-transform duration-500 ${outOfStock ? "" : "group-hover:scale-110"}`}
                        loading="lazy"
                      />
                    </div>
                    {/* Info */}
                    <div className="space-y-1">
                      {product.category && (
                        <p className="text-[10px] uppercase font-bold text-[var(--color-muted-foreground)] tracking-wider">{product.category}</p>
                      )}
                      <h3 className={`text-base font-bold line-clamp-2 leading-snug ${outOfStock ? "text-slate-400" : "text-foreground group-hover:text-brand-amber transition-colors"}`}>
                        {product.name}
                      </h3>
                      <div className="flex items-center justify-between pt-3 mt-2 border-t border-border">
                        {user ? (
                          <span className={`text-sm font-black ${outOfStock ? "text-slate-400" : "text-foreground"}`}>${product.price?.toFixed(2)}</span>
                        ) : (
                          <span className="text-xs font-medium text-[var(--color-muted-foreground)] italic">Login to view price</span>
                        )}
                        <div className={`p-2 rounded-lg transition-colors ${outOfStock ? "bg-muted text-slate-300 cursor-not-allowed" : "bg-muted group-hover:bg-brand-amber text-[var(--color-muted-foreground)] group-hover:text-white"}`}>
                          <LuShoppingCart size={16} />
                        </div>
                      </div>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center items-center gap-2 mt-14">
            <button
              onClick={() => goPage(Math.max(1, page - 1))}
              disabled={page === 1}
              className="w-10 h-10 flex items-center justify-center rounded-lg border border-border text-[var(--color-muted-foreground)] hover:bg-muted disabled:opacity-30 transition-colors"
            >
              <LuChevronLeft size={16} />
            </button>

            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter(n => n === 1 || n === totalPages || Math.abs(n - page) <= 1)
              .reduce((acc, n, i, arr) => {
                if (i > 0 && n - arr[i - 1] > 1) acc.push("...");
                acc.push(n);
                return acc;
              }, [])
              .map((item, i) =>
                item === "..." ? (
                  <span key={`e${i}`} className="px-1 text-brand-muted">…</span>
                ) : (
                  <button
                    key={item}
                    onClick={() => goPage(item)}
                    className={`w-10 h-10 flex items-center justify-center rounded-lg font-bold text-sm transition-colors ${
                      item === page
                        ? "bg-brand-amber text-white"
                        : "hover:bg-muted text-[var(--color-muted-foreground)] hover:text-foreground"
                    }`}
                  >
                    {item}
                  </button>
                )
              )}

            <button
              onClick={() => goPage(Math.min(totalPages, page + 1))}
              disabled={page === totalPages}
              className="w-10 h-10 flex items-center justify-center rounded-lg border border-border text-[var(--color-muted-foreground)] hover:bg-muted disabled:opacity-30 transition-colors"
            >
              <LuChevronRight size={16} />
            </button>
          </div>
        )}
      </main>
    </div>
  );
}
